import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export interface ReturnFlowResult {
  status: 'success' | 'error' | 'processing' | 'none';
  message?: string;
  accountStatus?: {
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requiresAction: boolean;
    actionNeeded?: string;
  };
  shouldRefresh?: boolean;
}

interface UseStripeReturnFlowOptions {
  organizationId?: string;
  stripeAccountId?: string;
  onStatusUpdate?: (status: ReturnFlowResult) => void;
}

export const useStripeReturnFlow = ({
  organizationId,
  stripeAccountId,
  onStatusUpdate
}: UseStripeReturnFlowOptions = {}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [returnResult, setReturnResult] = useState<ReturnFlowResult>({ status: 'none' });

  const processReturn = useCallback(async (returnType: string, errorDetails?: string) => {
    if (!organizationId || !stripeAccountId) {
      console.warn('Missing organizationId or stripeAccountId for return processing');
      return;
    }

    setIsProcessing(true);
    
    try {
      if (returnType === 'success') {
        // User returned successfully from Stripe onboarding
        console.log('Processing successful return from Stripe onboarding');
        
        // Immediately check the account status
        const { data: statusData, error: statusError } = await supabase.functions.invoke(
          'check-connect-account-status',
          {
            body: { 
              accountId: stripeAccountId,
              forceRefresh: true // Force a fresh check from Stripe
            }
          }
        );

        if (statusError) {
          throw new Error(`Status check failed: ${statusError.message}`);
        }

        const result: ReturnFlowResult = {
          status: 'success',
          accountStatus: {
            detailsSubmitted: statusData.status?.detailsSubmitted || false,
            chargesEnabled: statusData.status?.chargesEnabled || false,
            payoutsEnabled: statusData.status?.payoutsEnabled || false,
            requiresAction: statusData.status?.overall !== 'complete',
            actionNeeded: statusData.nextSteps?.[0]?.description
          },
          shouldRefresh: true
        };

        // Determine the appropriate success message
        if (statusData.status?.overall === 'complete') {
          result.message = 'Stripe onboarding completed successfully! You can now accept payments.';
          toast.success('Payment setup complete!', {
            description: 'Your Stripe account is fully configured and ready to accept payments.'
          });
        } else if (statusData.status?.detailsSubmitted) {
          result.message = 'Onboarding in progress. Your account is being reviewed by Stripe.';
          toast.success('Onboarding submitted!', {
            description: 'Your information has been submitted. Stripe will review your account shortly.'
          });
        } else {
          result.message = 'Additional information needed to complete setup.';
          toast.warning('Setup incomplete', {
            description: result.accountStatus.actionNeeded || 'Please complete the remaining requirements.'
          });
        }

        setReturnResult(result);
        onStatusUpdate?.(result);

      } else if (returnType === 'error') {
        // User returned with an error
        console.log('Processing error return from Stripe onboarding:', errorDetails);
        
        const result: ReturnFlowResult = {
          status: 'error',
          message: 'There was an issue with your Stripe onboarding. You can try again.',
          shouldRefresh: false
        };

        setReturnResult(result);
        onStatusUpdate?.(result);

        toast.error('Onboarding incomplete', {
          description: 'You can restart the onboarding process when you\'re ready.',
          action: {
            label: 'Try Again',
            onClick: () => {
              // Trigger onboarding restart - this will be handled by the parent component
              window.location.reload();
            }
          }
        });
      }

    } catch (error) {
      console.error('Error processing Stripe return:', error);
      
      const result: ReturnFlowResult = {
        status: 'error',
        message: 'Failed to check account status. Please refresh the page.',
        shouldRefresh: false
      };

      setReturnResult(result);
      onStatusUpdate?.(result);

      toast.error('Status check failed', {
        description: 'Unable to verify your account status. Please refresh the page.'
      });
    } finally {
      setIsProcessing(false);
    }
  }, [organizationId, stripeAccountId, onStatusUpdate]);

  const checkForReturnParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connectStatus = urlParams.get('connect');
    const error = urlParams.get('error');

    if (connectStatus || error) {
      console.log('Detected Stripe return parameters:', { connectStatus, error });
      
      // Clean the URL immediately to prevent reprocessing on refresh
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      
      // Process the return
      if (connectStatus === 'success') {
        processReturn('success');
      } else if (error || connectStatus === 'error') {
        processReturn('error', error || 'Unknown error');
      }
      
      return true; // Return true if we found and processed return params
    }
    
    return false; // No return params found
  }, [processReturn]);

  // Check for return parameters on mount and when dependencies change
  useEffect(() => {
    if (organizationId && stripeAccountId) {
      checkForReturnParams();
    }
  }, [organizationId, stripeAccountId, checkForReturnParams]);

  return {
    isProcessing,
    returnResult,
    checkForReturnParams,
    processReturn
  };
};